import { useMemo, useCallback, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { AlertTriangle, FilterX, PlayCircle, Target } from 'lucide-react';
import { useGraph, useLastResult, useRunErrors, useSimulationStore, useSelectedNodeId } from '../../../store/simulationStore';
import { useScenarioStore, useScenarios, useActiveScenario } from '../../../store/scenarioStore';
import type { SimulationNode } from '../../../models/simulation';
import type { ReactFlowInstance } from 'reactflow';
import { getAllDescendants } from '../../../utils/graph';
import { deriveFormulaUnit } from '../../../utils/formulaUnits';
import { formatReadableNumber } from '../../../utils/numberFormatting';
import { productMessaging } from '../../../config/messaging';

type Statistic = 'mean' | 'median' | 'p10' | 'p90' | 'std' | 'value';

const STATISTIC_SEQUENCE: Statistic[] = ['mean', 'median', 'p10', 'p90', 'std', 'value'];

const STATISTIC_LABELS: Record<Statistic, string> = {
  mean: 'Mean',
  median: 'Median',
  p10: 'P10',
  p90: 'P90',
  std: 'Std Dev',
  value: 'Raw Samples'
};

type OutputMetric = {
  id: string;
  name: string;
  description?: string;
  nodeType: 'distribution' | 'formula';
  statistic: Statistic;
  value: number | null;
  error: string | null;
  unit?: string;
  delta?: {
    absolute: number;
    percentage: number;
    baselineValue: number;
  };
};

interface OutputMetricsPanelProps {
  reactFlowInstance: ReactFlowInstance | null;
  onHoverMetric?: (nodeId: string | null) => void;
}

export const OutputMetricsPanel = ({ reactFlowInstance, onHoverMetric }: OutputMetricsPanelProps) => {
  const graph = useGraph();
  const result = useLastResult();
  const runErrors = useRunErrors();
  const selectedNodeId = useSelectedNodeId();
  const setSelection = useSimulationStore((s) => s.setSelection);
  const [statisticOverrides, setStatisticOverrides] = useState<Record<string, Statistic>>({});
  
  // Scenario management
  const scenarios = useScenarios();
  const activeScenario = useActiveScenario();
  const setActiveScenario = useScenarioStore((s) => s.setActiveScenario);
  const getScenarioResults = useScenarioStore((s) => s.getScenarioResults);
  
  // Get baseline scenario for delta comparison and as fallback
  const baselineScenario = scenarios.find(s => s.isBaseline);
  const baselineResults = baselineScenario ? getScenarioResults(baselineScenario.id) : null;
  
  // Use scenario results if a scenario is active, otherwise use baseline scenario results (which match the baseline simulation)
  // This ensures "Baseline Scenario" (empty value) and baseline scenario show the same data
  const displayResult = activeScenario 
    ? getScenarioResults(activeScenario.id) 
    : (baselineResults || result);
  
  const showDelta = activeScenario && !activeScenario.isBaseline && baselineResults;

  // Get all output nodes (nodes marked with isOutput)
  const allOutputNodes = useMemo<SimulationNode[]>(() => {
    return graph.nodes.filter((node: SimulationNode) => node.data.isOutput === true);
  }, [graph.nodes, graph.updatedAt]);

  // Filter output nodes based on selection
  const outputNodes = useMemo<SimulationNode[]>(() => {
    if (!selectedNodeId) {
      // No selection - show all output nodes
      return allOutputNodes;
    }

    const selectedNode = graph.nodes.find((n) => n.id === selectedNodeId);
    if (!selectedNode) {
      return allOutputNodes;
    }

    // Get all descendants (nodes that depend on the selected node)
    const descendants = getAllDescendants(selectedNodeId, graph.edges);
    
    // Include the selected node itself
    const relevantNodeIds = new Set([selectedNodeId, ...descendants]);

    // Filter output nodes that are the selected node or any of its descendants
    return allOutputNodes.filter((node) => {
      return relevantNodeIds.has(node.id);
    });
  }, [allOutputNodes, selectedNodeId, graph.edges, graph.nodes]);

  // Get results for output nodes
  const outputMetrics = useMemo<OutputMetric[]>(() => {
    if (!displayResult) return [];

    const mapped = outputNodes.map((node: SimulationNode): OutputMetric => {
      const nodeData = node.data;
      const statistic = statisticOverrides[node.id] ?? nodeData.statistic ?? 'mean';
      
      const nodeResult = displayResult.results[node.id];
      
      // Get unit from node or result
      let unit: string | undefined;
      if (nodeResult?.unit) {
        unit = nodeResult.unit;
      } else if (nodeData.type === 'distribution') {
        unit = nodeData.unit;
      } else if (nodeData.type === 'formula') {
        unit = deriveFormulaUnit(node, graph.nodes);
      }
      
      if (!nodeResult) {
        return {
          id: node.id,
          name: nodeData.name,
          description: nodeData.description,
          nodeType: nodeData.type,
          statistic,
          value: null,
          error: 'No result available',
          unit
        };
      }

      // Get the value based on the statistic type
      let value: number | null = null;
      switch (statistic) {
        case 'mean':
          value = nodeResult.summary?.mean;
          break;
        case 'median':
          value = nodeResult.summary?.median;
          break;
        case 'p10':
          value = nodeResult.summary?.p10;
          break;
        case 'p90':
          value = nodeResult.summary?.p90;
          break;
        case 'std':
          value = nodeResult.summary?.std;
          break;
        case 'value':
          value = nodeResult.summary?.mean; // For single values, use mean
          break;
        default:
          // Fallback to mean if statistic is somehow invalid
          value = nodeResult.summary?.mean;
          break;
      }

      // Validate that the value is a valid finite number
      if (value === null || value === undefined || !Number.isFinite(value)) {
        return {
          id: node.id,
          name: nodeData.name,
          description: nodeData.description,
          nodeType: nodeData.type,
          statistic,
          value: null,
          error: 'Invalid value',
          unit
        };
      }

      // Calculate delta if comparing to baseline
      let delta: OutputMetric['delta'] = undefined;
      if (showDelta && baselineResults && value !== null) {
        const baselineNodeResult = baselineResults.results[node.id];
        if (baselineNodeResult) {
          let baselineValue: number | null = null;
          switch (statistic) {
            case 'mean':
              baselineValue = baselineNodeResult.summary?.mean;
              break;
            case 'median':
              baselineValue = baselineNodeResult.summary?.median;
              break;
            case 'p10':
              baselineValue = baselineNodeResult.summary?.p10;
              break;
            case 'p90':
              baselineValue = baselineNodeResult.summary?.p90;
              break;
            case 'std':
              baselineValue = baselineNodeResult.summary?.std;
              break;
            case 'value':
              baselineValue = baselineNodeResult.summary?.mean;
              break;
          }
          
          if (baselineValue !== null && baselineValue !== undefined && Number.isFinite(baselineValue)) {
            const absoluteDelta = value - baselineValue;
            const percentageDelta = baselineValue !== 0 ? (absoluteDelta / baselineValue) * 100 : 0;
            delta = {
              absolute: absoluteDelta,
              percentage: percentageDelta,
              baselineValue
            };
          }
        }
      }

      return {
        id: node.id,
        name: nodeData.name,
        description: nodeData.description,
        nodeType: nodeData.type,
        statistic,
        value,
        error: null,
        unit,
        delta
      };
    });
    mapped.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return mapped;
  }, [outputNodes, displayResult, graph.nodes, showDelta, baselineResults, statisticOverrides]);

  const focusNode = useCallback((nodeId?: string) => {
    if (!nodeId) return;
    setSelection(nodeId);
    const rf = reactFlowInstance;
    try {
      if (rf && typeof (rf as any).fitView === 'function') {
        (rf as any).fitView?.({ nodes: [{ id: nodeId }], padding: 0.2, maxZoom: 1.5, duration: 500 });
      } else if (rf && typeof rf.setCenter === 'function') {
        const node = graph.nodes.find((n) => n.id === nodeId);
        if (node) {
          rf.setCenter(node.position.x + 160, node.position.y + 40, { zoom: 1.2, duration: 500 });
        }
      }
    } catch {
      // ignore
    }
  }, [graph.nodes, reactFlowInstance, setSelection]);

  const handleStatisticClick = useCallback((event: MouseEvent<HTMLButtonElement>, metric: OutputMetric) => {
    event.stopPropagation();
    const currentIndex = STATISTIC_SEQUENCE.indexOf(metric.statistic);
    const nextStatistic = STATISTIC_SEQUENCE[(currentIndex + 1) % STATISTIC_SEQUENCE.length];

    setStatisticOverrides((prev) => ({
      ...prev,
      [metric.id]: nextStatistic
    }));

    focusNode(metric.id);
  }, [focusNode]);

  const header = (
    <div className="panel-header">
      <h2>Output Metrics</h2>
    </div>
  );

  const renderEmptyState = (
    icon: ReactNode,
    title: string,
    body: string,
    action?: ReactNode
  ) => (
    <div className="panel output-metrics-panel">
      {header}
      <div className="panel-body output-empty-state">
        <div className="output-empty-icon" aria-hidden="true">{icon}</div>
        <h3>{title}</h3>
        <p>{body}</p>
        {action && <div className="output-empty-action">{action}</div>}
      </div>
    </div>
  );

  if (runErrors.length > 0) {
    return (
      <div className="panel output-metrics-panel">
        {header}
        <div className="panel-body">
          <p className="error panel-error-intro">Fix the following issues before running a simulation:</p>
          <div className="error-list">
            {runErrors.map((issue, index) => (
              <div key={`${issue.nodeId}-${issue.message}-${index}`} className="error-item">
                {issue.nodeId ? (
                  <button
                    type="button"
                    className="error-node-button"
                    onClick={() => focusNode(issue.nodeId)}
                  >
                    <span className="error-item-icon">⚠️</span>
                    <div className="error-item-details">
                      <span className="error-item-node">{issue.nodeName}</span>
                      <span className="error-item-message">{issue.message}</span>
                    </div>
                  </button>
                ) : (
                  <div className="error-item-content">
                    <span className="error-item-icon">⚠️</span>
                    <div className="error-item-details">
                      <span className="error-item-message">{issue.message}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (allOutputNodes.length === 0) {
    return renderEmptyState(
      <Target size={20} />,
      productMessaging.emptyStates.noOutputsTitle,
      productMessaging.emptyStates.noOutputsBody
    );
  }

  if (!displayResult) {
    return renderEmptyState(
      <PlayCircle size={20} />,
      productMessaging.emptyStates.noRunTitle,
      productMessaging.emptyStates.noRunBody
    );
  }

  if (outputMetrics.length === 0 && selectedNodeId) {
    return renderEmptyState(
      <FilterX size={20} />,
      productMessaging.emptyStates.filteredOutputsTitle,
      productMessaging.emptyStates.filteredOutputsBody,
      <button type="button" className="btn btn-secondary btn-compact" onClick={() => setSelection(undefined)}>
        Clear selection
      </button>
    );
  }

  if (outputMetrics.length === 0) {
    return renderEmptyState(
      <AlertTriangle size={20} />,
      'No output values available',
      'The model has outputs, but this run did not return usable metrics for them.'
    );
  }

  const filteredCount = outputMetrics.length;
  const totalCount = allOutputNodes.length;
  const isFiltered = selectedNodeId !== undefined && filteredCount < totalCount;

  return (
    <div className="panel output-metrics-panel">
      <div className="panel-header">
        <h2>Output Metrics</h2>
        <div className="panel-header-meta">
          {scenarios.length > 0 && (
            <select
              className="scenario-selector"
              value={activeScenario && !activeScenario.isBaseline ? activeScenario.id : ''}
              onChange={(e) => setActiveScenario(e.target.value || null)}
              title="Select scenario"
            >
              <option value="">Baseline Scenario</option>
              {scenarios
                .filter((scenario) => !scenario.isBaseline)
                .map((scenario) => (
                  <option key={scenario.id} value={scenario.id}>
                    {scenario.name}
                  </option>
                ))}
            </select>
          )}
          {isFiltered && (
            <span className="filter-indicator" title="Filtered by selected node">
              {filteredCount} of {totalCount} shown
            </span>
          )}
        </div>
      </div>
      <div className="panel-body">
        <div className="output-metrics-list">
          {outputMetrics.map((metric: OutputMetric) => (
            <div
              key={metric.id}
              className={`output-metric-card output-metric-card-${metric.nodeType}`}
              onClick={() => focusNode(metric.id)}
              onMouseEnter={() => onHoverMetric?.(metric.id)}
              onMouseLeave={() => onHoverMetric?.(null)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  focusNode(metric.id);
                }
              }}
            >
              <div className="output-metric-header">
                <h3 className="output-metric-name">{metric.name}</h3>
                {metric.description && (
                  <p className="output-metric-description">{metric.description}</p>
                )}
              </div>
              <div className="output-metric-details">
                <div className="output-metric-stat">
                  <span className="output-metric-label">Statistic:</span>
                  <button
                    type="button"
                    className="output-metric-stat-value output-metric-stat-button"
                    onClick={(event) => handleStatisticClick(event, metric)}
                    title="Click to cycle statistic"
                  >
                    {STATISTIC_LABELS[metric.statistic] ?? metric.statistic}
                  </button>
                </div>
              </div>
              <div className="output-metric-value-container">
                {metric.error ? (
                  <span className="output-metric-error">{metric.error}</span>
                ) : metric.value !== null ? (
                  <>
                    <span className="output-metric-value">
                      {formatReadableNumber(metric.value)}
                      {metric.unit && <span className="output-metric-unit"> {metric.unit}</span>}
                    </span>
                    {metric.delta && (
                      <div className="output-metric-delta">
                        <span className={`delta-indicator ${metric.delta.absolute >= 0 ? 'delta-positive' : 'delta-negative'}`}>
                          {metric.delta.absolute >= 0 ? '↑' : '↓'}
                          {' '}
                          {Math.abs(metric.delta.percentage).toFixed(1)}%
                          {' '}
                          ({metric.delta.absolute >= 0 ? '+' : ''}{formatReadableNumber(metric.delta.absolute)}
                          {metric.unit && ` ${metric.unit}`})
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <span className="output-metric-error">No value</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
